import { ReportJSON, isDefined } from '@aloft/toolkit-shared';

import {
  COST_APPROACH_SITE_COMMENT,
  getCostApproachReplacementCostComment,
} from '../../summaryV2/CostApproachSummary/consts.ts';
import { scalarToNumberToString } from '../utils.ts';
// eslint-disable-next-line aloft/prefer-sound-ui-box
import { createCheckBoxFieldNode } from './createCheckBoxFieldNode.ts';
import { createTextFieldNode } from './createTextFieldNode.ts';

export function generateTkv2CostDataRptiNodes(
  doc: Document,
  costApproach: ReportJSON['subject']['costApproach'],
): HTMLElement[] {
  const rptiNodes = [];

  const { remainingEcoLife, effectiveAge } = costApproach ?? {};

  if (isDefined(costApproach)) {
    rptiNodes.push(
      createCheckBoxFieldNode(doc, {
        Id: 'EstimatedReplacementCostNewCheckBox',
        Data: true,
      }),
    );

    // Cost Approach
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'CostDataSources',
        Data: costApproach.source?.name,
      }),
    );

    // cost data effective date
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'CostDataEffectiveDate',
        Data: costApproach.source?.effectiveDate.format('MM/dd/yyyy'),
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'CostServiceQualityRating',
        Data: costApproach.dwellingCostQuality,
      }),
    );

    // Estimated remaining economic life
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'EstimatedRemainingEconomicLife',
        Data: `${remainingEcoLife ?? ''}`,
      }),
    );

    // effective age
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'EffectiveAge',
        Data: `${effectiveAge ?? ''}`,
      }),
    );

    // opinion of site value
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'OpinionOfSiteValueAmount',
        Data: scalarToNumberToString(costApproach.site),
      }),
    );

    // gross living area
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'CostApproachGrossLivingArea',
        Data: scalarToNumberToString(costApproach.dwelling?.sqft),
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'SiteValueDwellingPricePerSqFtAmount',
        Data: scalarToNumberToString(costApproach.dwelling?.costPerSqft),
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'SiteValueDwellingAmount',
        Data: scalarToNumberToString(costApproach.dwelling?.totalCost),
      }),
    );

    // Basement
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'SiteValueBlankLabel',
        Data: costApproach.basement ? 'Basement' : '',
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'SiteValueBlankSqFt',
        Data: scalarToNumberToString(costApproach.basement?.sqft),
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'SiteValueBlankPricePerSqFtAmount',
        Data: scalarToNumberToString(costApproach.basement?.costPerSqft),
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'SiteValueBlankAmount',
        Data: scalarToNumberToString(costApproach.basement?.totalCost),
      }),
    );

    // Additional Structures
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'SiteValueBlank2Label',
        Data: costApproach?.additionalStructures ? 'Add. Struct. & Feats' : '',
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'SiteValueBlank2Amount',
        Data: scalarToNumberToString(costApproach.additionalStructures?.totalCost),
      }),
    );

    // Car Storage
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'SiteValueCarStorageSqFt',
        Data: scalarToNumberToString(costApproach.garageCarport?.sqft),
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'SiteValueCarStoragePricePerSqFtAmount',
        Data: scalarToNumberToString(costApproach.garageCarport?.costPerSqft),
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'SiteValueCarStorageAmount',
        Data: scalarToNumberToString(costApproach.garageCarport?.totalCost),
      }),
    );

    // TODO: Estimated Cost New Total Description
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'EstimatedCostNewTotalDescription',
      }),
    );

    // Depreciation
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'DepreciationPhysicalAmount',
        Data: scalarToNumberToString(costApproach.depreciation?.physical?.abs()),
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'DepreciationFunctionalAmount',
        Data: scalarToNumberToString(costApproach.depreciation?.functional?.abs()),
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'DepreciationExternalAmount',
        Data: scalarToNumberToString(costApproach.depreciation?.external?.abs()),
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'DepreciationTotalAmount',
        Data: scalarToNumberToString(costApproach.depreciation?.total.abs()),
      }),
    );

    // Indicate Value
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'IndicatedValueCostApproachAmount',
        Data: scalarToNumberToString(costApproach?.indicatedValue),
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'CostApproachComments',
        Data: getCostApproachReplacementCostComment(costApproach.source?.name ?? 'Craftsman'),
      }),
    );

    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'OpinionOfSiteValueSupport',
        Data: COST_APPROACH_SITE_COMMENT,
      }),
    );
  }

  if (costApproach?.depreciated) {
    // Improvement Depreciation
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'ImprovementsDepreciatedCostAmount',
        Data: scalarToNumberToString(costApproach.depreciated.cost),
      }),
    );
  }
  if (costApproach?.new) {
    // Estimated cost New Total Amount
    rptiNodes.push(
      createTextFieldNode(doc, {
        Id: 'EstimatedCostNewTotalAmount',
        Data: scalarToNumberToString(costApproach.new.cost),
      }),
    );
  }
  return rptiNodes;
}
